import type { ChatHistoryItem } from '../../api/chat-api';
import { ChatNoticeUtil } from '../../utils/chat-notice-util';

import type { SparkSession } from './spark-session.types';

const DRAFT_PREAMBLE = "Here's my edited version of the draft:";

export const SparkSessionUtil = {
  askAboutDraft({
    body,
    session,
  }: {
    body: string;
    session: Pick<SparkSession, 'chatFormRef' | 'status'>;
  }) {
    const { chatFormRef, status } = session;

    if (chatFormRef.current?.values.chat?.trim()) {
      status.setNotice(
        'Finish or clear the prompt in the box before bringing a draft into it.'
      );

      return;
    }

    void chatFormRef.current?.setFieldValue(
      'chat',
      `${DRAFT_PREAMBLE}\n\n${body.trim()}\n\n`
    );
  },

  editQueuedPrompt({
    promptId,
    session,
  }: {
    promptId: string;
    session: Pick<SparkSession, 'chatFormRef' | 'queue' | 'shelf' | 'status'>;
  }) {
    const { chatFormRef, queue, shelf, status } = session;

    if (
      chatFormRef.current?.values.chat?.trim() ||
      shelf.attachmentsRef.current.length > 0
    ) {
      status.setNotice(
        'Finish or clear the prompt in the box before editing a queued one.'
      );

      return;
    }

    const taken = queue.takeQueuedPrompt(promptId);

    if (!taken) return;

    if (taken.attachments.length > 0) shelf.putAttachments(taken.attachments);

    void chatFormRef.current?.setFieldValue('chat', taken.text);
  },

  selectChat({
    chatId,
    isFull = false,
    session,
  }: {
    chatId: ChatHistoryItem['id'] | null;
    isFull?: boolean;
    session: Pick<SparkSession, 'queue' | 'status' | 'transcript'>;
  }) {
    const { queue, status, transcript } = session;

    transcript.openChat({ chatId, isFull });

    const dropped = queue.resetQueue();

    status.setNotice(
      dropped > 0
        ? `${ChatNoticeUtil.droppedPrompts({ dropped })} - you moved to another chat.`
        : null
    );

    if (isFull) status.setNotice(ChatNoticeUtil.contextFull());
  },

  startNewChat({
    session,
  }: {
    session: Pick<
      SparkSession,
      | 'chatFormRef'
      | 'queue'
      | 'scrollRegionRef'
      | 'shelf'
      | 'status'
      | 'transcript'
    >;
  }) {
    const { chatFormRef, scrollRegionRef, shelf, transcript } = session;

    transcript.resetScreen();

    SparkSessionUtil.selectChat({ chatId: null, session });

    chatFormRef.current?.resetForm();
    shelf.clearAttachments();
    scrollRegionRef.current?.scrollToTop({ instant: true });
  },
};
